"use client";

import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { trackReviewSubmission } from "@/lib/services/analytics";
import { createClient } from "@/lib/supabase/client";
import type { Database } from "@/lib/types/supabase";
import { trpc } from "@/lib/trpc/react";

/**
 * Real-time show review hook
 *
 * Modern architecture:
 * - Database is the single source of truth
 * - Real-time events drive all updates
 * - Optimistic updates for instant feedback
 * - No cache invalidation needed (staleTime: Infinity)
 * - Handles both status toggles and full reviews
 */

// Custom event for syncing optimistic updates across component instances
const SHOW_REVIEW_EVENT = "show-review-update";

export interface ShowReview {
	id: string;
	showId: string;
	artistId: string | null;
	userId: string;
	rating: number;
	content: string | null;
	loved: boolean;
	sawLive: boolean;
	sawStream: boolean;
	wantToSee: boolean;
	mustSee: boolean;
	createdAt: string;
	updatedAt: string;
}

interface UseRealtimeShowReviewOptions {
	showId: string;
	artistId?: string | null;
	userId: string | null;
	enabled?: boolean;
}

export function useRealtimeShowReview({
	showId,
	artistId,
	userId,
	enabled = true,
}: UseRealtimeShowReviewOptions) {
	// Local state is our source of truth
	const [review, setReview] = useState<ShowReview | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const channelRef = useRef<any>(null);
	const instanceId = useRef(Math.random().toString(36).substring(7));
	const supabase = createClient();
	const utils = trpc.useUtils();

	// tRPC mutation for saving reviews
	const saveReviewMutation = trpc.show.saveShowReview.useMutation({
		onSuccess: async () => {
			const FORCE_REFETCH = { refetchType: 'all' as const };
			await Promise.all([
				utils.show.getUserShowReview.invalidate({ showId, userId: userId || "" }),
				utils.show.getCommunityShowReviews.invalidate({ showId }),
				utils.show.getFacepileData.invalidate(),
				utils.show.list.invalidate(),
				utils.show.getBySlug.invalidate(),
				...(artistId ? [
					utils.artist.getStats.invalidate({ artistId }),
					utils.artist.getShows.invalidate(undefined, FORCE_REFETCH),
					utils.artist.getCounts.invalidate({ artistId }),
				] : []),
				utils.user.getProfileShows.invalidate(undefined, FORCE_REFETCH),
				utils.user.getProfileArtists.invalidate(undefined, FORCE_REFETCH),
			]);
		},
		onError: (err) => {
			// On error, real-time will correct the state
			const errorMessage = err.message?.includes("UNAUTHORIZED")
				? "Please sign in to save your review"
				: err.message?.includes("Rate limit")
					? err.message
					: "Failed to save review";

			toast.error(errorMessage);
		},
	});

	// Initial data fetch - runs when showId changes
	useEffect(() => {
		let cancelled = false;

		if (!userId || !showId || !enabled) {
			setIsLoading(false);
			setReview(null);
			return;
		}

		async function fetchInitialReview() {
			if (!userId) return;

			try {
				type ShowReviewRow = Database["public"]["Tables"]["show_reviews"]["Row"];

				const { data, error } = await supabase
					.from("show_reviews")
					.select("*")
					.eq("user_id", userId)
					.eq("show_id", showId)
					.maybeSingle() as { data: ShowReviewRow | null; error: any };

				if (cancelled) return;

				if (!error && data) {
					setReview({
						id: data.id,
						showId: data.show_id,
						artistId: data.artist_id,
						userId: data.user_id,
						rating: data.rating,
						content: data.content,
						loved: data.loved,
						sawLive: data.saw_live,
						sawStream: data.saw_stream,
						wantToSee: data.want_to_see,
						mustSee: data.must_see,
						createdAt: data.created_at,
						updatedAt: data.updated_at,
					});
				} else {
					setReview(null);
				}
			} catch (error) {
				if (cancelled) return;
				console.error("Error fetching initial show review:", error);
				setReview(null);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		fetchInitialReview();
		return () => { cancelled = true; };
	}, [userId, showId, enabled, supabase]);

	// Real-time subscription - this is where all updates come from
	useEffect(() => {
		if (!userId || !showId || !enabled) return;

		// Unique channel per instance to avoid conflicts
		const channelName = `show-review:${showId}:${userId}:${instanceId.current}`;

		const channel = supabase
			.channel(channelName)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "show_reviews",
					filter: `user_id=eq.${userId}`,
				},
				(payload: RealtimePostgresChangesPayload<any>) => {
					// Client-side filter for show_id (Supabase Realtime only supports 1 filter)
					const record = payload.eventType === "DELETE" ? payload.old : payload.new;
					if (record?.show_id !== showId) {
						return;
					}

					// Real-time update from database

					// IMPORTANT: Longer delay to let optimistic updates take precedence
					// This prevents race conditions where real-time events override optimistic updates
					setTimeout(() => {
						if (payload.eventType === "DELETE") {
							setReview(null);
						} else if (payload.new) {
							setReview({
								id: payload.new.id,
								showId: payload.new.show_id,
								artistId: payload.new.artist_id,
								userId: payload.new.user_id,
								rating: payload.new.rating,
								content: payload.new.content,
								loved: payload.new.loved,
								sawLive: payload.new.saw_live,
								sawStream: payload.new.saw_stream,
								wantToSee: payload.new.want_to_see,
								mustSee: payload.new.must_see,
								createdAt: payload.new.created_at,
								updatedAt: payload.new.updated_at,
							});
						}
					}, 1000); // Longer delay to ensure optimistic updates have time to settle
				},
			)
			.subscribe((status) => {
				// Handle subscription errors gracefully - errors are silently handled
				if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
					// Will retry on reconnect
				}
			});

		channelRef.current = channel;

		return () => {
			if (channelRef.current) {
				supabase.removeChannel(channelRef.current);
			}
		};
	}, [userId, showId, enabled, supabase]);

	// Listen for optimistic updates from other component instances on the same page
	useEffect(() => {
		if (!userId || !showId) {
			return;
		}

		const handleOptimisticUpdate = (event: CustomEvent) => {
			const {
				userId: eventUserId,
				showId: eventShowId,
				review: eventReview,
			} = event.detail;

			// Check if this event is for our show
			if (eventUserId === userId && eventShowId === showId) {
				setReview(eventReview);
			}
		};

		window.addEventListener(
			SHOW_REVIEW_EVENT as any,
			handleOptimisticUpdate as any,
		);

		return () => {
			window.removeEventListener(
				SHOW_REVIEW_EVENT as any,
				handleOptimisticUpdate as any,
			);
		};
	}, [userId, showId]);

	// Ref-based debounce for server mutations — stable across re-renders.
	const pendingMutationRef = useRef<any>(null);
	const mutationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const flushMutation = useCallback(async () => {
		const data = pendingMutationRef.current;
		pendingMutationRef.current = null;
		if (!data) return;
		try {
			await saveReviewMutation.mutateAsync(data);
			if (data.rating > 0) {
				trackReviewSubmission(showId, data.rating, "show", artistId || undefined, !!data.content?.trim());
			}
		} catch {
			// Error handled in mutation onError / realtime will correct
		}
	}, [saveReviewMutation, showId, artistId]);

	const debounceMutation = useCallback((mutationData: any) => {
		pendingMutationRef.current = mutationData;
		if (mutationTimerRef.current) clearTimeout(mutationTimerRef.current);
		mutationTimerRef.current = setTimeout(flushMutation, 800);
	}, [flushMutation]);

	// Save review with optimistic updates
	const saveReview = async (data: {
		rating?: number;
		content?: string;
		loved?: boolean;
		sawLive?: boolean;
		sawStream?: boolean;
		wantToSee?: boolean;
		mustSee?: boolean;
	}, { immediate = false }: { immediate?: boolean } = {}) => {
		if (!userId) {
			toast.error("Please sign in to save your review");
			return;
		}

		// Merge with existing review data
		const updatedReview = review
			? {
					...review,
					...data,
					updatedAt: new Date().toISOString(),
				}
			: {
					id: `temp-${showId}`,
					showId,
					artistId: artistId || null,
					userId,
					rating: data.rating || 0,
					content: data.content || null,
					loved: data.loved || false,
					sawLive: data.sawLive || false,
					sawStream: data.sawStream || false,
					wantToSee: data.wantToSee || false,
					mustSee: data.mustSee || false,
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString(),
				};

		// Check if this should delete the review (all fields empty)
		const shouldDelete =
			updatedReview.rating === 0 &&
			!updatedReview.content?.trim() &&
			!updatedReview.loved &&
			!updatedReview.sawLive &&
			!updatedReview.sawStream &&
			!updatedReview.wantToSee &&
			!updatedReview.mustSee;

		// 1. Optimistic update - instant UI feedback
		const optimisticReview = shouldDelete ? null : updatedReview;
		setReview(optimisticReview);

		// 2. Broadcast to other instances on the page
		window.dispatchEvent(new CustomEvent(SHOW_REVIEW_EVENT, {
			detail: { userId, showId, review: optimisticReview },
		}));

		// 3. Send to database (debounced for rapid toggles, immediate for modal saves)
		const mutationData = {
			showId,
			artistId: artistId || undefined,
			rating: updatedReview.rating,
			content: updatedReview.content || "",
			loved: updatedReview.loved,
			sawLive: updatedReview.sawLive,
			sawStream: updatedReview.sawStream,
			wantToSee: updatedReview.wantToSee,
			mustSee: updatedReview.mustSee,
		};

		if (immediate) {
			if (mutationTimerRef.current) {
				clearTimeout(mutationTimerRef.current);
				pendingMutationRef.current = null;
			}
			try {
				await saveReviewMutation.mutateAsync(mutationData);
				if (!shouldDelete && updatedReview.rating > 0) {
					trackReviewSubmission(showId, updatedReview.rating, "show", artistId || undefined, !!updatedReview.content?.trim());
				}
			} catch {
				// Error handled in mutation onError
			}
		} else {
			debounceMutation(mutationData);
		}
	};

	// Toggle a single status flag (debounced)
	const toggleStatus = async (
		status: "wantToSee" | "mustSee" | "sawLive" | "sawStream",
		value?: boolean,
	) => {
		const currentValue = review?.[status] || false;
		const newValue = value !== undefined ? value : !currentValue;

		await saveReview({
			rating: review?.rating || 0,
			content: review?.content || "",
			loved: review?.loved || false,
			sawLive: review?.sawLive || false,
			sawStream: review?.sawStream || false,
			wantToSee: review?.wantToSee || false,
			mustSee: review?.mustSee || false,
			[status]: newValue,
		});
	};

	// Toggle loved status
	const toggleLoved = async () => {
		await saveReview({
			rating: review?.rating || 0,
			content: review?.content || "",
			loved: !review?.loved,
			sawLive: review?.sawLive || false,
			sawStream: review?.sawStream || false,
			wantToSee: review?.wantToSee || false,
			mustSee: review?.mustSee || false,
		});
	};

	// Delete review (immediate)
	const deleteReview = async () => {
		await saveReview({
			rating: 0,
			content: "",
			loved: false,
			sawLive: false,
			sawStream: false,
			wantToSee: false,
			mustSee: false,
		}, { immediate: true });
	};

	return {
		review,
		saveReview,
		deleteReview,
		toggleStatus,
		toggleLoved,
		isLoading,
		isPending: saveReviewMutation.isPending,
	};
}
